import { useEffect, useState, useCallback } from 'react'
import { projectService, workspaceService, userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import type { Project, Workspace, User } from '@/types'
import { projectsListConfig } from '@/lib/config/node-list-configs'
import { NodeListPage } from '@/components/nodes/NodeListPage'
import type { ProjectsFiltersState } from '@/components/projects'
import type { ProjectGroupByOption } from '@/lib/config/node-grouping-configs'

export default function ProjectsPage() {
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('my-incomplete')
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<ProjectGroupByOption>('none')

  // Initialize filters with default values
  const [filters, setFilters] = useState<ProjectsFiltersState>(projectsListConfig.defaultFilters)

  // Handler for partial filter updates
  const handleFiltersChange = (partialFilters: Partial<ProjectsFiltersState>) => {
    setFilters(
      (prev: ProjectsFiltersState) => ({ ...prev, ...partialFilters }) as ProjectsFiltersState
    )
  }

  const loadProjects = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      // Load projects, workspaces, and users in parallel
      const [allProjects, allWorkspaces, allUsers] = await Promise.all([
        projectService.getAll(),
        workspaceService.getAll(),
        userService.getAll(),
      ])

      // SuperAdmins see all projects, others see their own
      if (user.role === 'SuperAdmin') {
        setProjects(allProjects)
      } else {
        // Filter projects where user is creator or a member
        const userProjects = allProjects.filter(
          (project) => project.creator.id === user.id || project.users.some((u) => u.id === user.id)
        )
        setProjects(userProjects)
      }

      setWorkspaces(allWorkspaces)
      setUsers(allUsers)
    } catch (error) {
      console.error('Failed to load projects:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  return (
    <NodeListPage
      nodes={projects}
      loading={loading}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={
        {
          ...projectsListConfig,
          filterNodes: (
            nodes: Project[],
            tab: string,
            filterState: ProjectsFiltersState,
            query: string
          ) =>
            projectsListConfig.filterNodes(
              nodes,
              tab,
              filterState,
              query,
              user ? { id: user.id, role: user.role } : undefined
            ),
          filterComponentProps: {
            ...projectsListConfig.filterComponentProps,
            availableWorkspaces: workspaces,
            availableUsers: users,
          },
        } as typeof projectsListConfig
      }
    />
  )
}
